import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw, ListChecks, LayoutGrid, Loader2, Target, Zap } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, formatDate } from "@/lib/api";
import { pct, quizSubtitle, startGeneral, startPersonal } from "@/lib/quiz";
import { QuizReview } from "@/components/quiz/QuizReview";

function weakAreas(attempt) {
  const map = {};
  attempt.questions.forEach((q, i) => {
    const key = q.category_label;
    map[key] = map[key] || { label: key, category: q.category, missed: 0, total: 0 };
    map[key].total += 1;
    if (attempt.answers[i] !== q.correct_index) map[key].missed += 1;
  });
  return Object.values(map).filter((a) => a.missed > 0).sort((a, b) => b.missed - a.missed);
}

function verdict(p) {
  if (p >= 90) return "Excellent";
  if (p >= 70) return "Solid work";
  if (p >= 50) return "Getting there";
  return "Good start";
}

export function QuizResults({ attempt }) {
  const navigate = useNavigate();
  const [review, setReview] = useState(false);
  const [focus, setFocus] = useState(null);
  const [busy, setBusy] = useState(null);
  const total = attempt.questions.length;
  const p = pct(attempt.score, total);
  const areas = weakAreas(attempt);

  const restart = async (key, fn) => {
    setBusy(key);
    try {
      const a = await fn();
      navigate(`/quiz/${a.id}`, { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(null);
    }
  };
  const again = () =>
    restart("again", () => (attempt.kind === "personal" ? startPersonal() : startGeneral(attempt.topic, attempt.difficulty, attempt.length || 10)));

  return (
    <div className="space-y-5 rise" data-testid="quiz-results">
      <section className="card p-6 text-center relative overflow-hidden">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full bg-[#E50914]/15 blur-3xl pointer-events-none" />
        <div className="eyebrow mb-2">{attempt.title} · {quizSubtitle(attempt)}</div>
        <div className="font-display text-6xl font-bold leading-none" data-testid="quiz-results-score">{attempt.score}<span className="text-[#52525B]">/{total}</span></div>
        <div className="text-lg font-semibold mt-2" data-testid="quiz-results-percent">{p}% · {verdict(p)}</div>
        <div className="flex justify-center gap-6 mt-4 text-sm">
          <span className="text-[#10B981] font-semibold" data-testid="quiz-results-correct">{attempt.score} correct</span>
          <span className="text-[#EF4444] font-semibold" data-testid="quiz-results-incorrect">{total - attempt.score} incorrect</span>
        </div>
        {attempt.kind === "general" && (
          <div className="inline-flex items-center gap-1.5 mt-4 px-3 h-8 rounded-full bg-[#E50914]/10 border border-[#E50914]/40 text-sm font-bold" data-testid="quiz-results-xp">
            <Zap size={14} className="text-[#E50914] fill-[#E50914]" /> +{attempt.xp_earned || 0} XP
          </div>
        )}
        {attempt.kind === "general" && !attempt.xp_earned && attempt.score > 0 && <div className="text-[11px] text-[#71717A] mt-2">XP is earned the first time you answer each question correctly.</div>}
        {attempt.completed_at && <div className="text-xs text-[#71717A] mt-3">{formatDate(attempt.completed_at.slice(0, 10))}</div>}
      </section>

      {areas.length > 0 && (
        <section data-testid="quiz-weak-areas">
          <div className="eyebrow mb-2.5">Areas to improve</div>
          <div className="card divide-y divide-[#1F1F23]">
            {areas.map((a, n) => (
              <div key={a.label} className="flex items-center gap-3 px-4 py-3">
                <Target size={16} className="text-[#F59E0B] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{a.label}</div>
                  <div className="text-xs text-[#71717A]">{a.missed} of {a.total} missed</div>
                </div>
                <button data-testid={`quiz-why-${a.category}-${n}`} onClick={() => { setFocus(focus === a.label ? null : a.label); setReview(false); }} className={`h-9 px-3 rounded-lg border text-xs font-semibold transition-colors shrink-0 ${focus === a.label ? "border-[#E50914] text-white" : "border-[#27272A] hover:border-[#E50914]"}`}>
                  {focus === a.label ? "Hide" : "See why"}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
      {focus && <QuizReview attempt={attempt} onlyMissed={focus} />}

      <div className="grid grid-cols-2 gap-2.5">
        <button data-testid="quiz-review-button" onClick={() => { setReview(!review); setFocus(null); }} className="btn-secondary"><ListChecks size={16} /> {review ? "Hide review" : "Review answers"}</button>
        <button data-testid="quiz-again-button" onClick={again} disabled={!!busy} className="btn-secondary">{busy === "again" ? <Loader2 size={16} className="animate-spin" /> : <><RotateCcw size={16} /> {attempt.kind === "personal" ? "Retake" : "New quiz"}</>}</button>
      </div>
      {review && <QuizReview attempt={attempt} />}
      <button data-testid="quiz-another-button" onClick={() => navigate("/quizzes")} className="btn-primary"><LayoutGrid size={16} /> Choose another quiz</button>
    </div>
  );
}
